import { atom, selector } from 'recoil';
import { Filters, ICustomer, Sort, SortDirection } from '../lib/types';
import { customersArchiveAtom } from './customer-archive';
import { customersAtom } from './customers';

// currently applied filter
export const customersFilterAtom = atom<Filters>({
   key: 'CustomerListFilter',
   default: Filters.all,
});

export const attorneyFiltersAtom = atom<string[]>({
   key: 'attorneyFilters',
   default: [],
 });

// currently applied sort
export const customersSortAtom = atom<Sort>({
   key: 'CustomerListSort',
   default: Sort.firstName,
});

// currently applied sort direction (ascending / descending)
export const sortDirectionAtom = atom<SortDirection>({
   key: 'SortDirection',
   default: SortDirection.ascending,
});

export const archivedCustomersSelector = selector({
   key: 'ArchivedCustomerList',
   get: ({ get }): ICustomer[] => {
      const showArchive = get(customersArchiveAtom);
      const customers = get(customersAtom);

      return customers.filter((customer) => !customer.archived || showArchive);
   },
});

// filters customers list based on customersFilterAtom, customersArchiveAtom
export const filteredCustomersSelector = selector({
   key: 'FilteredCustomerList',
   get: ({ get }): ICustomer[] => {
      const filter = get(customersFilterAtom);
      const customers = get(archivedCustomersSelector);
      const selectedAttorneys = get(attorneyFiltersAtom);

      let filtered = [...customers];

      switch (filter) {
         case Filters.newClients:
            filtered = filtered.filter((item) => item.newCustomer);
            break;
         case Filters.recentViolations:
            filtered = filtered.filter((item) => item.recentViolation);
            break;
         case Filters.alerts:
            filtered = filtered.filter((item) => item.alert);
            break;
         case Filters.overdueCalibrations:
            filtered = filtered.filter((item) => item.calibrationOverdue);
            break;
         default:
            break;
      }

      // Attorney filter (multi-select support)
      if (selectedAttorneys.length > 0) {
         filtered = filtered.filter((item) =>
            selectedAttorneys.includes(item.attorneyName)
         );
      }

      return filtered;
   },
});

// sorts filtered customers list based on customerSortAtom and customerSortDirectionAtom
export const filteredSortedCustomersSelector = selector({
   key: 'FilteredSortedCustomerList',
   get: ({ get }): ICustomer[] => {
      const sort = get(customersSortAtom);
      const customers = get(filteredCustomersSelector);
      const direction = get(sortDirectionAtom);

      let arrayForSort = [...customers];

      switch (sort) {
         case Sort.firstName:
            arrayForSort.sort((a, b) =>
               a.firstName.localeCompare(b.firstName)
            );
            break;

         case Sort.lastName:
            arrayForSort.sort((a, b) =>
               a.lastName.localeCompare(b.lastName)
            );
            break;

         case Sort.email:
            arrayForSort.sort((a, b) =>
               a.email.localeCompare(b.email)
            );
            break;

         case Sort.numberOfViolations:
            arrayForSort.sort((a, b) => {
               const numA = parseInt(a.numberOfViolations || '0', 10);
               const numB = parseInt(b.numberOfViolations || '0', 10);
               return numA - numB;
            });
            break;

         case Sort.mostRecentViolation:
            arrayForSort.sort((a, b) => {
               const dateA = a.mostRecentViolationUtc ? new Date(a.mostRecentViolationUtc).getTime() : 0;
               const dateB = b.mostRecentViolationUtc ? new Date(b.mostRecentViolationUtc).getTime() : 0;
               return dateA - dateB;
            });
            break;

         case Sort.nextCalibrationDate:
            arrayForSort.sort((a, b) => {
               const dateA = a.nextCalibrationDateUtc ? new Date(a.nextCalibrationDateUtc).getTime() : 0;
               const dateB = b.nextCalibrationDateUtc ? new Date(b.nextCalibrationDateUtc).getTime() : 0;
               return dateA - dateB;
            });
            break;

         case Sort.leaseDate:
            arrayForSort.sort((a, b) => {
               const dateA = a.leaseSignedUtc ? new Date(a.leaseSignedUtc).getTime() : 0;
               const dateB = b.leaseSignedUtc ? new Date(b.leaseSignedUtc).getTime() : 0;
               return dateA - dateB;
            });
            break;

         case Sort.removalDate:
            arrayForSort.sort((a, b) => {
               const dateA = a.removalDateUtc ? new Date(a.removalDateUtc).getTime() : 0;
               const dateB = b.removalDateUtc ? new Date(b.removalDateUtc).getTime() : 0;
               return dateA - dateB;
            });
            break;

         default:
            return customers;
      }

      return direction === SortDirection.ascending
         ? arrayForSort
         : arrayForSort.reverse();
   },
});

